import Swal from "sweetalert2";
import ApiService, { HttpMethod } from "../../../../commons/api/api_service";
import BaseResponse from "../../../../commons/api/base_response";
import { Endpoints } from "../../../../commons/api/endpoints";
import { ProviderVM } from "../../../../commons/base/providers/view_model";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../../../commons/scaffold/scaffold";
import {
    LocationModel,
    ReceiptAdjustmentModel,
    ReceiptDetailResponse,
    ReceiptResponse,
    SubmitReceiptAdjustmentModel,
    SubmitReceiptAdjustmentResponse,
} from "../models/adjustment_forms_vendor_receipt_models";
import StringUtils from "../../../../commons/utils/string_utils";

interface ItemOptions {
    id: number;
    item: string;
    materialType: string;
    smallUnit?: string;
    bigUnit?: string;
}

export default class AdjustmentFormsVendorReceiptViewModel extends ProviderVM {
    // UI States
    isLoading: boolean = false;
    showSnackbar?: ShowSnackbarModel;
    showErrorView?: ShowErrorViewModel;
    errorMessage: string = "";
    isShowModal: boolean = false;

    // Data
    locationOptions: LocationModel[] = [];
    filterLocationId?: number;
    receiptDataList: ReceiptResponse[] = [];
    filteredReceiptDataList: ReceiptResponse[] = [];
    itemOptions: ItemOptions[] = [];
    selectedItemId?: number;
    searchQuery: string = "";

    formId?: number;
    formLocationId?: number;
    formReceivedAt?: string;
    receiptDataMap: Map<string, ReceiptAdjustmentModel> = new Map<
        string,
        ReceiptAdjustmentModel
    >();

    constructor() {
        super();
        this.setup();
    }

    setup() {
        this.fetchReceiptList();
        this.fetchLocationOptions();
        this.fetchItemOptions();
    }

    // Fetch data
    fetchReceiptList(locationId?: number) {
        this.setIsLoading(true);

        ApiService.fetchWithQueryParams(
            Endpoints.adjustmentFormGetReceiptList,
            {
                httpMethod: HttpMethod.get,
                queryParameters: {
                    ...(locationId && { locationId }),
                },
                onSuccess: (response: BaseResponse<ReceiptResponse[]>) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        const receiptList = response.data as ReceiptResponse[];
                        this.setReceiptList(receiptList);
                        this.setFilteredReceiptList(receiptList);
                    }
                },
                onFailure: (error) => {
                    this.setIsLoading(false);
                    console.log(error);
                    this.setErrorMessage(
                        "Failed fetching data, please refresh or try again"
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    fetchReceiptDetail(receiptId: number) {
        this.setIsLoading(true);
        ApiService.fetch(
            `${Endpoints.adjustmentFormGetReceiptDetail}/${receiptId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<ReceiptDetailResponse[]>
                ) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        const items = response.data as ReceiptDetailResponse[];
                        const receiptMap = new Map<
                            string,
                            ReceiptAdjustmentModel
                        >();
                        items.forEach((item) => {
                            const uniqueId = `${item.rowId}-${item.masterItemId}`;
                            receiptMap.set(uniqueId, {
                                rowId: item.rowId,
                                itemId: item.masterItemId,
                                uniqueId,
                                isNew: false,
                                bigQty: item.qtyBigUnit,
                                smallQty: item.qtySmallUnit,
                                itemName: item.masterItemName,
                                bigUnit: item.bigUnit, // only to show in modal
                                smallUnit: item.smallUnit, // only to show in modal
                            });
                        });
                        this.setReceiptDataMap(receiptMap);
                    } else {
                        console.log(response);
                        this.setErrorMessage(response.message);
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                                this.setup();
                            },
                            message: this.errorMessage,
                        });
                    }
                },
                onFailure: (error) => {
                    this.setIsLoading(false);
                    console.log(error);
                    this.setErrorMessage(
                        "Failed fetching data, please refresh or try again"
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    fetchLocationOptions() {
        ApiService.fetch(Endpoints.adjustmentFormGetLocationOptions, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<LocationModel[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    this.setLocationOptions(response.data as LocationModel[]);
                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                        this.setup();
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    fetchItemOptions() {
        this.setIsLoading(true);
        ApiService.fetch(Endpoints.adjustmentFormGetItemOptions, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<ItemOptions[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    const itemOptions = response.data as ItemOptions[];
                    this.setItemOptions(itemOptions);
                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setup();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setup();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    // Data Setters
    setReceiptList(value: ReceiptResponse[]) {
        this.emit(() => {
            this.receiptDataList = value;
        });
    }

    setFilteredReceiptList(value: ReceiptResponse[]) {
        this.emit(() => {
            this.filteredReceiptDataList = value;
        });
    }

    setLocationOptions(value: LocationModel[]) {
        this.emit(() => {
            this.locationOptions = value;
        });
    }

    setFilterLocationId(value?: number) {
        this.emit(() => {
            this.filterLocationId = value;
        });
    }

    setItemOptions(value: ItemOptions[]) {
        this.emit(() => {
            this.itemOptions = value;
        });
    }

    setSelectedItemId(value?: number) {
        this.emit(() => {
            this.selectedItemId = value;
        });
    }

    setSearchQuery(value: string) {
        this.emit(() => {
            this.searchQuery = value;
        });
    }

    setFormId(value?: number) {
        this.emit(() => {
            this.formId = value;
        });
    }

    setFormLocationId(value?: number) {
        this.emit(() => {
            this.formLocationId = value;
        });
    }

    setFormReceivedAt(value?: string) {
        // Adjust date from UTC to UTC+7 when receive from backend / change from date time picker
        let adjustedDate: string | undefined;
        if (value) {
            adjustedDate = StringUtils.UTCtoLocale(value);
        }
        this.emit(() => {
            this.formReceivedAt = adjustedDate;
        });
    }

    setReceiptDataMap(value: Map<string, ReceiptAdjustmentModel>) {
        this.emit(() => {
            this.receiptDataMap = value;
        });
    }

    handleChangeItemData(
        uniqueId: string, // id of form_receipt_items
        field: keyof ReceiptAdjustmentModel,
        newValue: number
    ) {
        const updatedMap = new Map(this.receiptDataMap);
        const oldData = updatedMap.get(uniqueId);
        if (oldData) {
            updatedMap.set(uniqueId, { ...oldData, [field]: newValue });
            this.setReceiptDataMap(updatedMap);
        }
    }

    handleAddItem(itemId: number) {
        const updatedMap = new Map(this.receiptDataMap);
        const selectedOption = this.itemOptions.find(
            (option) => option.id === itemId
        );

        const randomNumber = Math.random();

        const uniqueId = `${randomNumber}-${selectedOption?.id}`;
        if (!updatedMap.has(uniqueId)) {
            updatedMap.set(uniqueId, {
                rowId: randomNumber, // unset id of form_receipt_items
                itemId,
                uniqueId,
                isNew: true,
                bigQty: 0,
                smallQty: 0,
                itemName: selectedOption?.item,
                bigUnit: selectedOption?.bigUnit,
                smallUnit: selectedOption?.smallUnit,
            });
        }
        this.setReceiptDataMap(updatedMap);
        this.setSearchQuery(selectedOption?.item.toLowerCase() ?? "");
    }

    handleSubmit() {
        Swal.fire({
            title: "Konfirmasi",
            text: "Anda yakin ingin melakukan adjustment ini?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Gass",
        }).then(({ isConfirmed }) => {
            if (isConfirmed) {
                if (
                    !this.formId ||
                    !this.formLocationId ||
                    !this.formReceivedAt
                ) {
                    this.setShowErrorView({
                        message: "Data tidak lengkap!",
                        action: () => {
                            this.setShowErrorView(undefined);
                        },
                    });
                    return;
                }

                this.setIsLoading(true);

                const items = [...this.receiptDataMap.values()].map(
                    (value): ReceiptAdjustmentModel => ({
                        rowId: value.rowId, // id of form_receipt_items
                        uniqueId: value.uniqueId,
                        isNew: value.isNew,
                        itemId: value.itemId,
                        bigQty: value.bigQty,
                        smallQty: value.smallQty,
                    })
                );

                // Set time to UTC before send to backend
                const submitData: SubmitReceiptAdjustmentModel = {
                    formId: this.formId,
                    formLocation: this.formLocationId,
                    formType: "RECEIPT",
                    receivedAt: StringUtils.LocaleToUTC(this.formReceivedAt),
                    items,
                };

                ApiService.fetch(Endpoints.adjustmentFormSubmit, {
                    httpMethod: HttpMethod.post,
                    parameters: submitData,
                    onSuccess: (
                        response: BaseResponse<SubmitReceiptAdjustmentResponse>
                    ) => {
                        this.setIsLoading(false);
                        if (response.code === 200) {
                            const data =
                                response.data as SubmitReceiptAdjustmentResponse;
                            console.log(data);
                            this.showSnackbar = new ShowSnackbarModel({
                                message: "Success Adjust Receipt Form",
                                type: "normal",
                            });
                        } else {
                            console.log(response);
                            this.setErrorMessage(response.message);
                            this.setShowErrorView({
                                action: () => {
                                    this.reset();
                                    this.setup();
                                    this.setShowErrorView(undefined);
                                    this.setErrorMessage("");
                                },
                                message: this.errorMessage,
                            });
                        }
                        this.setIsShowModal(false);
                        this.resetFormData();
                        this.setup();
                    },
                    onFailure: (error) => {
                        console.log(error);
                        this.setIsLoading(false);
                        this.setErrorMessage(
                            "Failed fetching data, please refresh or try again"
                        );
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setup();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                            },
                            message: this.errorMessage,
                        });
                    },
                });
            }
        });
    }

    resetFormData() {
        this.emit(() => {
            this.formId = undefined;
            this.formLocationId = undefined;
            this.receiptDataMap = new Map<string, ReceiptAdjustmentModel>();
            this.formReceivedAt = undefined;
            this.searchQuery = "";
        });
    }

    // UI Setters
    setIsLoading(value: boolean) {
        this.emit(() => {
            this.isLoading = value;
        });
    }

    setErrorMessage(value: string) {
        this.emit(() => {
            this.errorMessage = value;
        });
    }

    setShowErrorView(value?: ShowErrorViewModel) {
        this.emit(() => {
            this.showErrorView = value;
        });
    }

    setShowSnackbar(value: ShowSnackbarModel) {
        this.emit(() => {
            this.showSnackbar = value;
        });
    }

    setIsShowModal(value: boolean) {
        this.emit(() => {
            this.isShowModal = value;
        });
    }

    reset() {
        this.emit(() => {
            this.isLoading = false;
            this.errorMessage = "";
            this.showErrorView = undefined;
            this.showSnackbar = undefined;
            this.receiptDataList = [];
            this.filteredReceiptDataList = [];
            this.formId = undefined;
            this.formLocationId = undefined;
            this.formReceivedAt = undefined;
            this.locationOptions = [];
            this.filterLocationId = undefined;
            this.receiptDataMap = new Map<string, ReceiptAdjustmentModel>();
            this.itemOptions = [];
            this.selectedItemId = undefined;
            this.searchQuery = "";
        });
    }
}
